/* import __COLOCATED_TEMPLATE__ from './connect.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import type FreshdeskConfiguration from 'embercom/models/standalone/freshdesk-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  stackVertically?: boolean;
  hideBorder?: boolean;
  hideTitle?: boolean;
  closeOnSave?: () => void;
  closeOnRevoke?: () => void;
}

export default class StandaloneFreshdeskConnect extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare intl: IntlService;
  @service declare notificationsService: {
    notifyConfirmation: (message: string) => void;
    notifyResponseError: (
      e: Error,
      messages: { default: string },
      options: { responseProvidedErrors: number[] },
    ) => void;
  };

  @tracked isSaving = false;

  @action async save() {
    this.isSaving = true;
    try {
      await this.finStandaloneService.saveFreshdeskConfig();
      this.args.closeOnSave?.();
    } finally {
      this.isSaving = false;
    }
  }

  @action async disableTickets() {
    let isConfirmed = await this.intercomConfirmService.confirm({
      body: this.intl.t('standalone.freshdesk.settings.connect.disable-tickets-confirm'),
      confirmButtonText: this.intl.t(
        'standalone.freshdesk.settings.connect.disable-tickets-confirm-button',
      ),
    });

    if (!isConfirmed) {
      return;
    }

    this.isSaving = true;
    try {
      await this.freshdeskConfig.deactivateForTickets();
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.freshdesk.deploy.tickets.activation.deactivation-success'),
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.freshdesk.deploy.tickets.activation.deactivation-error'),
        },
        { responseProvidedErrors: RESPONSE_PROVIDED_ERRORS },
      );
      console.error(e.message || e.jqXHR);
    } finally {
      this.isSaving = false;
    }
  }

  @action async revokeAccess() {
    let isConfirmed = await this.intercomConfirmService.confirm({
      body: this.intl.t('standalone.freshdesk.settings.connect.confirm'),
      confirmButtonText: this.intl.t('standalone.freshdesk.settings.connect.confirm-button'),
    });

    try {
      if (isConfirmed) {
        await this.freshdeskConfig.revoke();
        if (this.args.closeOnRevoke) {
          this.args.closeOnRevoke();
        }
        this.notificationsService.notifyConfirmation(
          this.intl.t('standalone.freshdesk.settings.connect.revoke-success-notify'),
        );
      }
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        { default: this.intl.t('standalone.freshdesk.settings.connect.revoke-failure-notify') },
        { responseProvidedErrors: RESPONSE_PROVIDED_ERRORS },
      );
      console.error(e.message || e.jqXHR);
    }
  }

  get freshdeskConfig(): FreshdeskConfiguration {
    return this.finStandaloneService.freshdeskConfig!;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Freshdesk::Settings::Connect': typeof StandaloneFreshdeskConnect;
    'standalone/freshdesk/settings/connect': typeof StandaloneFreshdeskConnect;
  }
}
